"use client";

import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Settings2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { AddresseeGender, SpeakerGender, TranslationContext } from "@/app/[locale]/api/translate/route";
import { getTranslationSettingsSummary } from "@/app/[locale]/api/translate/utility";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { X } from "lucide-react";
import { useTranslations } from 'next-intl';

interface TranslationSettingsProps {
  disabled?: boolean;
  context?: TranslationContext;
  onChange?: (context: TranslationContext) => void;
}

export function TranslationSettings({
  disabled,
  context: initialContext = {},
  onChange
}: TranslationSettingsProps) {
  const t = useTranslations('translationSettings');

  const [isOpen, setIsOpen] = useState(false);

  // Helper functions for cleaner initialization
  function getFormality(ctx: TranslationContext) {
    return ctx.isFormal === true ? "formal" :
      ctx.isFormal === false ? "informal" : undefined;
  }

  function getAddresseeCount(ctx: TranslationContext) {
    return ctx.isGroup === true ? "multiple" :
      ctx.isGroup === false ? "single" : undefined;
  }

  const [formality, setFormality] = useState<string | undefined>(
    getFormality(initialContext)
  );
  const [speakerGender, setSpeakerGender] = useState<SpeakerGender>(
    initialContext.speakerGender
  );
  const [addresseeCount, setAddresseeCount] = useState<string | undefined>(
    getAddresseeCount(initialContext)
  );
  const [addresseeGender, setAddresseeGender] = useState<AddresseeGender>(
    initialContext.addresseeGender
  );
  const [contextText, setContextText] = useState(initialContext.context || "");

  // Update state values when initialContext changes
  useEffect(() => {
    setFormality(getFormality(initialContext));
    setSpeakerGender(initialContext.speakerGender);
    setAddresseeCount(getAddresseeCount(initialContext));
    setAddresseeGender(initialContext.addresseeGender);
    setContextText(initialContext.context || "");  // Add the empty string fallback
  }, [initialContext]);

  // Add a ref to prevent excessive updates
  const prevSummaryRef = useRef<string | undefined>(undefined);

  // Build human-readable summary of selected options
  const getSummary = () => {
    var summary = getTranslationSettingsSummary(
      formality,
      speakerGender,
      addresseeCount,
      addresseeGender,
      t // Pass the translator function
    );

    if (contextText && contextText.length > 0) {
      if (summary)
        summary += `\n${t('contextPrefix')}: ${contextText}`;
      else
        summary = `${t('contextPrefix')}: ${contextText}`;

      // Add a period at the end if it doesn't have one
      if (!contextText.endsWith(".")) {
        summary += ".";
      }
    }

    return summary;
  };

  const summary = getSummary();

  // Update parent component when settings change - with important optimizations
  useEffect(() => {
    if (onChange) {
      const newContext = {
        isFormal: formality === "formal" ? true :
          formality === "informal" ? false :
            undefined,
        speakerGender: speakerGender,
        isGroup: addresseeCount === "multiple" ? true :
          addresseeCount === "single" ? false :
            undefined,
        addresseeGender: addresseeGender,
        context: contextText && contextText.length > 0 ? contextText : undefined
      };

      // Only call onChange if the context actually changed
      if (summary !== prevSummaryRef.current) {
        prevSummaryRef.current = summary;
        onChange(newContext);
      }
    }
  }, [formality, speakerGender, addresseeCount, addresseeGender, contextText, onChange, summary]);

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button
          variant={summary ? "default" : "outline"}
          size="icon"
          disabled={disabled}
        >
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="inline-flex h-full w-full items-center justify-center">
                  <Settings2 className="h-4 w-4" />
                </span>
              </TooltipTrigger>
              <TooltipContent className="max-w-[300px] text-sm">
                {summary ?? t('defaultSettings')}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md w-full md:w-[400px] p-0 max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader className="sticky top-0 p-3 border-b">
          <DialogTitle>{t('dialogTitle')}</DialogTitle>
          <DialogDescription className="sr-only">
            {t('dialogDescription')}
          </DialogDescription>
        </DialogHeader>

        <div className="p-4 pt-0 space-y-4 overflow-y-auto">
          <div className="space-y-3 [&_button[role=radio]]:h-8">
            {/* Setting 1: Formality */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">{t('formalityLabel')}</Label>
              <Card className="p-1 bg-secondary/10">
                <ToggleGroup
                  type="single"
                  value={formality || ""}
                  onValueChange={(value) => setFormality(value === formality ? undefined : value)}
                  className="justify-start"
                >
                  <ToggleGroupItem value="formal" className="flex-1 c">{t('formalOption')}</ToggleGroupItem>
                  <ToggleGroupItem value="informal" className="flex-1">{t('informalOption')}</ToggleGroupItem>
                </ToggleGroup>
              </Card>
            </div>

            {/* Setting 2: Speaker Gender */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">{t('speakerGenderLabel')}</Label>
              <Card className="p-1 bg-secondary/10">
                <ToggleGroup
                  type="single"
                  value={speakerGender}
                  onValueChange={(value) => setSpeakerGender(value === speakerGender ? undefined : value as SpeakerGender)}
                  className="justify-start"
                >
                  <ToggleGroupItem value="masculine" className="flex-1">{t('maleOption')}</ToggleGroupItem>
                  <ToggleGroupItem value="feminine" className="flex-1">{t('femaleOption')}</ToggleGroupItem>
                  <ToggleGroupItem value="neutral" className="flex-1">{t('neutralOption')}</ToggleGroupItem>
                </ToggleGroup>
              </Card>
            </div>

            {/* Setting 3: Addressee Count */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">{t('addresseeLabel')}</Label>
              <Card className="p-1 bg-secondary/10">
                <ToggleGroup
                  type="single"
                  value={addresseeCount || ""}
                  onValueChange={(value) => setAddresseeCount(value === addresseeCount ? undefined : value)}
                  className="justify-start"
                >
                  <ToggleGroupItem value="single" className="flex-1">{t('singlePersonOption')}</ToggleGroupItem>
                  <ToggleGroupItem value="multiple" className="flex-1">{t('multiplePersonOption')}</ToggleGroupItem>
                </ToggleGroup>
              </Card>
            </div>

            {/* Setting 4: Addressee Gender */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">
                {addresseeCount === "multiple" ? t('whoAreLabel') : t('whoIsLabel')}
              </Label>
              <Card className="p-1 bg-secondary/10">
                <ToggleGroup
                  type="single"
                  value={addresseeGender}
                  onValueChange={(value) => setAddresseeGender(value === addresseeGender ? undefined : value as AddresseeGender)}
                  className="justify-start"
                >
                  <ToggleGroupItem value="masculine" className="flex-1">{t('maleOption')}</ToggleGroupItem>
                  <ToggleGroupItem value="feminine" className="flex-1">{t('femaleOption')}</ToggleGroupItem>
                  <ToggleGroupItem value="neutral" className="flex-1">{t('neutralOption')}</ToggleGroupItem>
                  {addresseeCount === "multiple" && (
                    <ToggleGroupItem value="mixed" className="flex-1">{t('mixedOption')}</ToggleGroupItem>
                  )}
                </ToggleGroup>
              </Card>
            </div>

            {/* Context field */}
            <div className="space-y-2 pt-2">
              <Label htmlFor="context" className="text-sm font-medium">
                {t('contextLabel')}
              </Label>
              <div className="flex w-full items-center space-x-2">
                <div className="relative w-full">
                  <Input
                    id="context"
                    value={contextText}
                    onChange={(e) => setContextText(e.target.value)}
                    placeholder={t('contextPlaceholder')}
                    className="w-full pr-8"
                    autoComplete="off"
                  />
                  {contextText && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
                      onClick={() => setContextText("")}  // Use empty string instead of undefined
                    >
                      <span className="sr-only">{t('clearButton')}</span>
                      <X className="h-4 w-4 text-muted-foreground" />
                    </Button>
                  )}
                </div>
              </div>
            </div>

            {/* Summary display */}
            <div className="my-4">
              <div className="text-sm font-medium mb-1">{t('currentSettings')}</div>
              <p className="text-sm text-muted-foreground">{summary}</p>
            </div>

          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}